/**
 * Service API d'authentification
 */

import { api, ApiResponse } from './api';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  RefreshTokenResponse,
  User,
  TestUser,
} from '../types/auth';

export const authApi = {
  /**
   * Connexion utilisateur
   */
  login: async (data: LoginRequest): Promise<ApiResponse<LoginResponse>> => {
    return api.post<LoginResponse>('/api/auth/login', data);
  },

  /**
   * Inscription (FrontOffice uniquement)
   */
  register: async (data: RegisterRequest): Promise<ApiResponse<RegisterResponse>> => {
    return api.post<RegisterResponse>('/api/auth/register', data);
  },

  /**
   * Vérification email
   */
  verifyEmail: async (data: VerifyEmailRequest): Promise<ApiResponse<VerifyEmailResponse>> => {
    return api.post<VerifyEmailResponse>('/api/auth/verify-email', data);
  },

  /**
   * Demande de réinitialisation de mot de passe
   */
  forgotPassword: async (data: ForgotPasswordRequest): Promise<ApiResponse<ForgotPasswordResponse>> => {
    return api.post<ForgotPasswordResponse>('/api/auth/forgot-password', data);
  },

  /**
   * Réinitialisation du mot de passe
   */
  resetPassword: async (data: ResetPasswordRequest): Promise<ApiResponse<ResetPasswordResponse>> => {
    return api.post<ResetPasswordResponse>('/api/auth/reset-password', data);
  },

  /**
   * Changement de mot de passe (utilisateur connecté)
   */
  changePassword: async (data: ChangePasswordRequest): Promise<ApiResponse<ChangePasswordResponse>> => {
    return api.post<ChangePasswordResponse>('/api/auth/change-password', data);
  },

  /**
   * Rafraîchir le token d'accès
   */
  refreshToken: async (refreshToken: string): Promise<ApiResponse<RefreshTokenResponse>> => {
    return api.post<RefreshTokenResponse>('/api/auth/refresh', { refreshToken });
  },

  /**
   * Déconnexion
   */
  logout: async (refreshToken: string): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    return api.post('/api/auth/logout', { refreshToken });
  },

  /**
   * Obtenir l'utilisateur courant
   */
  getCurrentUser: async (): Promise<ApiResponse<{ user: User }>> => {
    return api.get<{ user: User }>('/api/auth/me');
  },

  /**
   * Liste des utilisateurs de test (développement uniquement)
   */
  getTestUsers: async (): Promise<ApiResponse<{ users: TestUser[] }>> => {
    return api.get<{ users: TestUser[] }>('/api/auth/test-users');
  },

  /**
   * Mettre à jour le profil de l'utilisateur connecté
   */
  updateProfile: async (data: {
    firstName?: string;
    lastName?: string;
    username?: string;
  }): Promise<ApiResponse<{ user: User; message: string }>> => {
    return api.put<{ user: User; message: string }>('/api/auth/profile', data);
  },

  /**
   * Générer une URL présignée pour uploader un avatar
   */
  generateAvatarUploadUrl: async (): Promise<
    ApiResponse<{ uploadUrl: string; fileKey: string; publicUrl: string; expiresIn: number }>
  > => {
    return api.post<{ uploadUrl: string; fileKey: string; publicUrl: string; expiresIn: number }>(
      '/api/auth/profile/avatar/upload-url',
      {}
    );
  },

  /**
   * Mettre à jour l'URL de l'avatar après upload S3
   */
  updateAvatar: async (avatarUrl: string): Promise<ApiResponse<{ user: User; message: string }>> => {
    return api.put<{ user: User; message: string }>('/api/auth/profile/avatar', { avatarUrl });
  },

  /**
   * Changer l'email de l'utilisateur connecté
   */
  changeEmail: async (
    newEmail: string,
    currentEmail: string
  ): Promise<ApiResponse<{ user: User; message: string }>> => {
    return api.put<{ user: User; message: string }>('/api/auth/profile/email', {
      newEmail,
      currentEmail,
    });
  },
};
